/**
 * Toast Redux Actions
 * 
 * Provides action creators for managing toasts.
 * Uses the ToastFactory for creating toast objects.
 * 
 * @example
 * // Using factory-based creators (recommended)
 * dispatch(showSuccessToast('Saved', 'Changes saved successfully'));
 * dispatch(showErrorToast('Error', 'Something went wrong'));
 * 
 * @example
 * // Using factory directly for more control
 * dispatch(showToast(
 *   ToastFactory.warning('Warning', 'Please review')
 *     .withActions([{ label: 'Review', onClick: handleReview }])
 *     .build()
 * ));
 */

import { types } from './toastTypes';
import ToastFactory from './toastFactory';

// ============================================================================
// Core Actions
// ============================================================================

/**
 * Show a toast notification
 * @param {Object} toast - Toast object (use ToastFactory to create)
 * @returns {Object} Redux action
 */
export const showToast = (toast) => ({
    type: types.SHOW_TOAST,
    payload: toast,
});

/**
 * Dismiss a specific toast by ID
 * @param {string} id - Toast ID to dismiss
 * @returns {Object} Redux action
 */
export const dismissToast = (id) => ({
    type: types.DISMISS_TOAST,
    payload: id,
});

/**
 * Dismiss all active toasts
 * @returns {Object} Redux action
 */
export const dismissAllToasts = () => ({
    type: types.DISMISS_ALL_TOASTS,
});

/**
 * Update an existing toast
 * @param {string} id - Toast ID to update
 * @param {Object} updates - Properties to update
 * @returns {Object} Redux action
 */
export const updateToast = (id, updates) => ({
    type: types.UPDATE_TOAST,
    payload: { id, updates },
});

// ============================================================================
// Convenience Action Creators (Factory Pattern)
// ============================================================================

/**
 * Show a success toast
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showSuccessToast = (title, content = '', actions = []) => {
    const builder = ToastFactory.success(title, content);
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

/**
 * Show an error toast (longer duration by default)
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showErrorToast = (title, content = '', actions = []) => {
    const builder = ToastFactory.error(title, content);
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

/**
 * Show a warning toast
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showWarningToast = (title, content = '', actions = []) => {
    const builder = ToastFactory.warning(title, content);
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

/**
 * Show an info toast
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showInfoToast = (title, content = '', actions = []) => {
    const builder = ToastFactory.info(title, content);
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

/**
 * Show a normal/default toast
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showNormalToast = (title, content = '', actions = []) => {
    const builder = ToastFactory.normal(title, content);
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

// ============================================================================
// Advanced Action Creators
// ============================================================================

/**
 * Show a persistent toast (won't auto-close)
 * @param {string} type - Toast type (success, error, warning, info, normal)
 * @param {string} title - Toast title
 * @param {string} [content] - Toast content
 * @param {Array} [actions] - Action buttons
 * @returns {Object} Redux action
 */
export const showPersistentToast = (type, title, content = '', actions = []) => {
    const builder = ToastFactory.create(type, title, content).persistent();
    if (actions.length > 0) {
        builder.withActions(actions);
    }
    return showToast(builder.build());
};

/**
 * Show a toast with undo action (common pattern)
 * @param {string} title - Toast title
 * @param {string} content - Toast content
 * @param {Function} onUndo - Undo callback
 * @returns {Object} Redux action
 */
export const showUndoToast = (title, content, onUndo) => {
    return showToast(
        ToastFactory.success(title, content)
            .withAction('Undo', onUndo)
            .long()
            .build()
    );
};

export default {
    showToast,
    dismissToast,
    dismissAllToasts,
    updateToast,
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,
    showNormalToast,
    showPersistentToast,
    showUndoToast,
};

